import React, { useEffect, useState } from 'react';
import { Typography, Box, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import AIFeedbackGroup from '../components/AIFeedback/AIFeedbackGroup';
import Layout from '../components/Layout';
import { useParams } from 'react-router-dom';
import { getAiModels, getAIReviewedUVIQuestions, setTargetAccent } from '../service/service';

const AIFeedbacksTable = () => {
  const { courseId, courseName } = useParams(); // Get the course ID from the URL parameters
  const [aiFeedbacks, setAiFeedbacks] = useState([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [emailFilter, setEmailFilter] = useState('');
  const [selectedAccent, setSelectedAccent] = useState('');
  const [aiModels, setAiModels] = useState([]);
  
  const getAllAIFeedbacks = async () => {
    try {
      const result = await getAIReviewedUVIQuestions(courseId, courseName);
      setAiFeedbacks(result);
      setFilteredFeedbacks(result);
    } catch (error) {
      console.log('Error', error);
    }
  };

  const getAllAiModels = async () => {
    try {
      const response = await getAiModels();
      if (response.aiModels && response.aiModels.models) {
        setAiModels(response.aiModels.models || []);
        setSelectedAccent(response.aiModels.targetAccent || '');
      }
    } catch (error) {
      console.log('Error fetching AI models:', error);
    }
  };

  useEffect(() => {
    getAllAiModels();
  }, []);

  const selectAccent = async (accent) => {
    try {
      const targetAccent = accent;
      await setTargetAccent({ targetAccent });
      setSelectedAccent(accent);
    } catch (error) {
      console.log('Error setting target accent:', error);
    }
  };

  useEffect(() => {
    getAllAIFeedbacks();
  }, [courseId]);

  // Filter feedbacks based on email search
  useEffect(() => {
    if (!emailFilter.trim()) {
      setFilteredFeedbacks(aiFeedbacks);
    } else {
      const filtered = aiFeedbacks.filter(feedback => 
        feedback.userId?.email?.toLowerCase().includes(emailFilter.toLowerCase())
      );
      setFilteredFeedbacks(filtered);
    }
  }, [emailFilter, aiFeedbacks]);
  
  const handleDelete = (feedbackId) => {
    getAllAIFeedbacks();
  };

  const handleAccentChange = (event) => {
    const accent = event.target.value;
    selectAccent(accent);
  };

  const groupedFeedbacks = filteredFeedbacks?.reduce((acc, feedback) => {
    const { parentUnitId } = feedback;
    if (!acc[parentUnitId.name]) acc[parentUnitId.name] = [];
    acc[parentUnitId.name].push(feedback);
    return acc;
  }, {});

  return (
    <Layout>
      <Typography variant="h4" component="h1" sx={{ mb: 4 }}>
        Review AI Feedbacks
      </Typography>
      
      {/* Filter and Accent Selection Row */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, alignItems: 'center' }}>
        {/* Email Filter */}
        <TextField
          sx={{ maxWidth: 400 }}
          fullWidth
          placeholder="Search by email..."
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
        />

        {/* Accent Selector */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="accent-select-label">Target Accent</InputLabel>
          <Select
            labelId="accent-select-label"
            id="accent-select"
            value={selectedAccent}
            label="Target Accent"
            onChange={handleAccentChange}
          >
            {aiModels.map((accent) => (
              <MenuItem key={accent} value={accent}>
                {accent}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {Object.keys(groupedFeedbacks).map((unitName) => (
        <AIFeedbackGroup 
          handleDelete={handleDelete} 
          setFeedbacks={setAiFeedbacks} 
          key={unitName} 
          unitName={unitName} 
          feedbacks={groupedFeedbacks[unitName]} 
        />
      ))}
    </Layout>
  );
};

export default AIFeedbacksTable;